var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { normalize } from './normalize';
import { defaultRelationForDataType, relationBehavior, relationForSymbol, relationsForDataType, } from './relations';
/**
 * Label normalization for matching: besides diacritics/case folding it drops
 * punctuation that users typically leave out (`Autor, název` ~ `autor nazev`).
 */
export var normalizeLabel = function (label) {
    return normalize(label.replace(/[,;/]/g, ' '));
};
var labelWords = function (label) {
    return normalizeLabel(label).split(' ').filter(Boolean);
};
var DEFAULT_CONNECTORS = {
    and: ['and', 'a'],
    or: ['or', 'nebo'],
    not: ['not', 'ne'],
};
var DEFAULT_BETWEEN_SEPARATORS = ['a', 'and'];
var TRUE_WORDS = ['ano', 'yes', 'true', '1'];
var FALSE_WORDS = ['ne', 'no', 'false', '0'];
/**
 * Longest match of a word sequence starting at `from` against candidates.
 * Candidates are provided as (words → entry) pairs; quoted tokens match
 * a whole candidate label as a single token.
 */
var matchWordSequence = function (tokens, from, candidates) {
    var best;
    for (var _i = 0, candidates_1 = candidates; _i < candidates_1.length; _i++) {
        var candidate = candidates_1[_i];
        var wordCount = candidate.words.length;
        var first = tokens[from];
        if (wordCount === 0) {
            // An empty label can match nothing.
        }
        else if (first && first.type === 'quoted') {
            // A single quoted token may carry the whole label.
            if (normalizeLabel(first.value) === candidate.words.join(' ')) {
                if (!best || best.consumed < 1) {
                    best = { entry: candidate.entry, consumed: 1 };
                }
            }
        }
        else if (from + wordCount <= tokens.length) {
            var matches = true;
            for (var offset = 0; offset < wordCount; offset += 1) {
                var token = tokens[from + offset];
                if (token.type !== 'word' || normalizeLabel(token.value) !== candidate.words[offset]) {
                    matches = false;
                    break;
                }
            }
            if (matches && (!best || wordCount > best.consumed)) {
                best = { entry: candidate.entry, consumed: wordCount };
            }
        }
    }
    return best;
};
var connectorSet = function (kind, config) {
    var _a, _b;
    return new Set(__spreadArray(__spreadArray([], DEFAULT_CONNECTORS[kind], true), ((_b = (_a = config.connectorLabels) === null || _a === void 0 ? void 0 : _a[kind]) !== null && _b !== void 0 ? _b : []), true).map(normalize));
};
var resolvePseudoField = function (config, translate) {
    var _a;
    var label = (_a = config.label) !== null && _a !== void 0 ? _a : (config.labelKey ? translate(config.labelKey) : config.key);
    return {
        key: config.key,
        label: label,
        words: labelWords(label),
        values: config.values.map(function (value) {
            var _a;
            var valueLabel = (_a = value.label) !== null && _a !== void 0 ? _a : (value.labelKey ? translate(value.labelKey) : value.id);
            return { id: value.id, label: valueLabel, words: labelWords(valueLabel) };
        }),
    };
};
/**
 * Resolved, matchable view of the server search configuration. Everything is
 * translated once at construction; matching is diacritics/case-insensitive.
 */
var TslRegistry = /** @class */ (function () {
    function TslRegistry(config) {
        var _this = this;
        var _a, _b, _c, _d, _e;
        /** CZNA-217: dynamic fields resolved so far, by normalized word (null = not a field). */
        this.dynamicFields = new Map();
        this.translate = config.translate;
        this.resolveDynamicField = config.resolveDynamicField;
        this.negation = (_a = config.negation) !== null && _a !== void 0 ? _a : 'relation';
        this.checkDisjunction = (_b = config.checkDisjunction) !== null && _b !== void 0 ? _b : true;
        this.fieldNeedsOperator = (_c = config.fieldNeedsOperator) !== null && _c !== void 0 ? _c : false;
        this.fields = config.fields.map(function (field) { return _this.resolveField(field); });
        this.pseudoFields = ((_d = config.pseudoFields) !== null && _d !== void 0 ? _d : []).map(function (pseudo) {
            return resolvePseudoField(pseudo, config.translate);
        });
        this.fieldCandidates = this.fields.flatMap(function (field) {
            return field.aliasWordForms.map(function (words) { return ({ words: words, entry: field }); });
        });
        this.pseudoCandidates = this.pseudoFields.map(function (pseudo) { return ({
            words: pseudo.words,
            entry: pseudo,
        }); });
        this.connectorWords = {
            and: connectorSet('and', config),
            or: connectorSet('or', config),
            not: connectorSet('not', config),
        };
        this.betweenSeparators = new Set(((_e = config.betweenSeparators) !== null && _e !== void 0 ? _e : DEFAULT_BETWEEN_SEPARATORS).map(normalize));
        this.equalityWords = new Set(['STR_EQ', 'NUM_EQ', 'DAT_EQ', 'BOOL_EQ'].map(function (code) {
            return normalizeLabel(config.translate(code));
        }));
    }
    /**
     * True for tokens usable as the equality operator of a pseudo clause
     * (`:`/`=` symbols or a translated "je"/"is" word).
     */
    TslRegistry.prototype.isEqualityToken = function (token) {
        if (!token) {
            return false;
        }
        if (token.type === 'symbol') {
            return token.value === ':' || token.value === '=';
        }
        return token.type === 'word' && this.equalityWords.has(normalizeLabel(token.value));
    };
    Object.defineProperty(TslRegistry.prototype, "translateFn", {
        /** The translate function the registry was built with. */
        get: function () {
            return this.translate;
        },
        enumerable: false,
        configurable: true
    });
    /** Field by `search_field_item` id. */
    TslRegistry.prototype.fieldById = function (id) {
        var configured = this.fields.find(function (field) { return field.id === id; });
        if (configured) {
            return configured;
        }
        for (var _i = 0, _a = Array.from(this.dynamicFields.values()); _i < _a.length; _i++) {
            var dynamic = _a[_i];
            if (dynamic && dynamic.id === id) {
                return dynamic;
            }
        }
        return undefined;
    };
    TslRegistry.prototype.pseudoFieldByKey = function (key) {
        return this.pseudoFields.find(function (pseudo) { return pseudo.key === key; });
    };
    TslRegistry.prototype.matchConnector = function (token) {
        if (!token || token.type !== 'word') {
            return undefined;
        }
        var word = normalize(token.value);
        if (this.connectorWords.or.has(word)) {
            return 'or';
        }
        if (this.connectorWords.and.has(word)) {
            return 'and';
        }
        if (this.connectorWords.not.has(word)) {
            return 'not';
        }
        return undefined;
    };
    TslRegistry.prototype.matchField = function (tokens, from, options) {
        var configured = matchWordSequence(tokens, from, this.fieldCandidates);
        if (configured || (options === null || options === void 0 ? void 0 : options.dynamic) === false) {
            return configured;
        }
        // CZNA-217: a single unquoted word may still be a dynamic field (a MARC address).
        var token = tokens[from];
        if (!this.resolveDynamicField || !token || token.type !== 'word') {
            return undefined;
        }
        var dynamic = this.dynamicField(token.value);
        return dynamic ? { entry: dynamic, consumed: 1 } : undefined;
    };
    /** CZNA-217: resolves (and caches) a dynamic field for a word. */
    TslRegistry.prototype.dynamicField = function (word) {
        var _a, _b;
        var cacheKey = normalize(word);
        if (!this.dynamicFields.has(cacheKey)) {
            var config = (_a = this.resolveDynamicField) === null || _a === void 0 ? void 0 : _a.call(this, word);
            this.dynamicFields.set(cacheKey, config ? this.resolveField(config) : null);
        }
        return (_b = this.dynamicFields.get(cacheKey)) !== null && _b !== void 0 ? _b : undefined;
    };
    TslRegistry.prototype.matchPseudoField = function (tokens, from) {
        return matchWordSequence(tokens, from, this.pseudoCandidates);
    };
    TslRegistry.prototype.matchOperator = function (field, tokens, from) {
        var token = tokens[from];
        if (!token) {
            return undefined;
        }
        if (token.type === 'symbol') {
            var code_1 = relationForSymbol(token.value, field.dataType, field.relationSet);
            if (!code_1) {
                return undefined;
            }
            var relation = field.relations.find(function (candidate) { return candidate.code === code_1; });
            return relation ? { entry: relation, consumed: 1 } : undefined;
        }
        return matchWordSequence(tokens, from, field.relations.map(function (relation) { return ({ words: relation.words, entry: relation }); }));
    };
    TslRegistry.prototype.matchPseudoValue = function (pseudo, tokens, from) {
        return matchWordSequence(tokens, from, pseudo.values.map(function (value) { return ({ words: value.words, entry: value }); }));
    };
    /** Parses a boolean word (`ano`/`ne`/`yes`/…) to its canonical value. */
    TslRegistry.prototype.parseBooleanWord = function (word) {
        var normalized = normalize(word);
        if (TRUE_WORDS.includes(normalized)) {
            return 'true';
        }
        if (FALSE_WORDS.includes(normalized)) {
            return 'false';
        }
        return undefined;
    };
    TslRegistry.prototype.resolveField = function (config) {
        var _this = this;
        var _a, _b, _c;
        var label = this.translate(config.nameKey);
        var available = config.availableRelations && config.availableRelations.length > 0
            ? config.availableRelations
            : relationsForDataType(config.dataType);
        var relations = available.map(function (code) {
            var relationLabel = _this.translate(code);
            return {
                code: code,
                label: relationLabel,
                words: labelWords(relationLabel),
                behavior: relationBehavior(code),
            };
        });
        var aliasLabels = ((_a = config.aliasKeys) !== null && _a !== void 0 ? _a : []).map(function (key) { return _this.translate(key); });
        return {
            id: config.id,
            key: config.key,
            label: label,
            words: labelWords(label),
            dataType: config.dataType,
            hasCodebook: (_b = config.hasCodebook) !== null && _b !== void 0 ? _b : false,
            defaultRelation: (_c = config.defaultRelation) !== null && _c !== void 0 ? _c : defaultRelationForDataType(config.dataType),
            relations: relations,
            relationSet: new Set(available),
            aliasWordForms: __spreadArray([label], aliasLabels, true).map(labelWords)
                .filter(function (words) { return words.length > 0; }),
        };
    };
    return TslRegistry;
}());
export { TslRegistry };
